import React, { useEffect, useState } from "react";

import { Virtuoso } from "react-virtuoso";

interface Props {
  messagesCount: number;
  renderMessage: (index: number) => React.ReactNode;
  loadMore?: () => Promise<number>;
}

const MessageList: React.FC<Props> = ({
  messagesCount,
  renderMessage,
  loadMore = () => Promise.resolve(0),
}) => {
  // The idea of these numbers is to facilitate reverse scrolling. Our messages
  // query returns messages array ordered by latest first. In our UI, messages
  // are aligned to bottom, and to view older messages you have to scroll up.
  // HTML is top down, so in order to put newer messages at the bottom you have
  // to reverse what we received from the query. But we can instead generate
  // fake indexes that are already reversed, and then map those indexes to their
  // correct index. Like this:
  // item index: 9999, 9998, 9997, 9996, 9995, 9994, 9993, 9992, 9991, ...
  // true index:    0,    1,    2,    3,    4,    5,    6,    7,    8, ...

  // To start, we'll choose a high number to start our indexes counting down, in
  // our case 10000. We'll then provide react-virtuoso with `firstItemIndex`
  // prop equal to the number of items we have to render. For example, rendering
  // 50 messages, the prop value will be 10000 - 50 = 9950. Virtuoso will then
  // render items with indexes starting from 9950 until 9999. Because we want to
  // rendered 50 items, react-virtuoso will iterate from 9950 until it rendered
  // 50 items, stopping at index 9999. It looks like this:
  // 9950, 9951, 9952, 9953, 9954, ... , 9995, 9996, 9997, 9998, 9999
  // 9950 will map to the last message (oldest) and 9999 will map to the
  // first message (latest), via some math (START_INDEX - index - 1).

  // On load more, the `firstItemIndex` value will be deducted with the number
  // of new items. In the above example, we started with 10000 and want to
  // render 50 items, so `firstItemIndex` is 9950. Upon scrolling to the very
  // top, we'll load 25 more items. After fetching, we'll deduct 25 from
  // `firstItemIndex`, it will be 9950 - 25 = 9925. react-virtuoso will then now
  // render indexes starting from 9925 until 9999, still stopping at index 9999
  // because the number of items to render is now 75, 50 from initial + 25 from
  // load more.

  // `initialTopMostItemIndex` is used to scroll to the last item on render,
  // which is the first and latest message.

  // This is how react-virtuoso wants to implement reverse infinite scrolling in
  // their docs, we modified this a bit to be compatible with latest first
  // sorting because their docs gave the example with an older first sort.

  // We didn't do flipping the container (like scaleY(-1)) because it would lead
  // to a non-obvious scrolling direction, scrolling up with the mouse/touchpad
  // will make you scroll down instead.
  const START_INDEX = 10000;
  const [firstItemIndex, setFirstItemIndex] = useState(0);
  useEffect(() => {
    setFirstItemIndex(START_INDEX - messagesCount);
  }, [messagesCount]);

  return (
    <Virtuoso
      firstItemIndex={firstItemIndex}
      initialTopMostItemIndex={{ index: messagesCount - 1, align: "end" }}
      totalCount={messagesCount}
      itemContent={(index) => {
        const trueIndex = START_INDEX - index - 1;
        return renderMessage(trueIndex);
      }}
      startReached={() => {
        loadMore().then((newItemsCount) => {
          setFirstItemIndex((value) => value - newItemsCount);
        });
      }}
      alignToBottom
    />
  );
};

export default MessageList;
